import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { waitlistApi } from '../services/api';
import { SurveyModal } from './SurveyModal';
import { useAnalytics } from '../hooks/useAnalytics';
import { getErrorType } from '../utils/analyticsHelpers';
import { FEATURE_FLAGS } from '../constants/featureFlags';

const WaitlistForm = ({ variant = 'default', className = '', formPosition = 'unknown' }) => {
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [profileToken, setProfileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(''); // 'success', 'error', or ''
  const [message, setMessage] = useState('');
  const [showSurvey, setShowSurvey] = useState(false);

  const hasTrackedStart = useRef(false);
  const emailInputRef = useRef(null);
  // Emails already submitted in this session. Guards against duplicate signups
  // (double-clicks, resubmits, multiple forms on the page) re-triggering Brevo's
  // Double Opt-In "confirm subscription" email for the same address.
  const submittedEmailsRef = useRef(new Set());
  const analytics = useAnalytics();

  const handleEmailFocus = () => {
    // Track start only once per session
    if (!hasTrackedStart.current) {
      analytics.trackWaitlistStart(formPosition);
      hasTrackedStart.current = true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    // Skip if this email was already submitted in this session — avoids sending
    // a duplicate confirmation email for the same address.
    if (submittedEmailsRef.current.has(normalizedEmail)) {
      setStatus('success');
      setMessage("You're already on the waitlist!");
      return;
    }

    setIsSubmitting(true);
    setStatus('');
    setMessage('');

    // Track submit event
    analytics.trackWaitlistSubmit(formPosition, email);

    try {
      const result = await waitlistApi.subscribe(email);
      setStatus('success');
      setMessage(result.message || 'Successfully added to waitlist!');

      // Track success event
      analytics.trackWaitlistSuccess(formPosition, result);

      // Show survey modal and clear email on successful submission
      if (result.success) {
        console.log('Subscription successful');

        // Remember this email so a repeat submit doesn't re-trigger DOI.
        submittedEmailsRef.current.add(normalizedEmail);
        
        // Only show survey modal if feature flag is enabled
        if (FEATURE_FLAGS.SURVEY_MODAL_ENABLED) {
          console.log('Survey modal enabled, showing modal');
          setSubmittedEmail(email);
          setProfileToken(result.profile_token || '');
          setShowSurvey(true);
        } else {
          console.log('Survey modal disabled via feature flag');
        }
        
        setEmail('');

        // Reset tracking for next submission
        hasTrackedStart.current = false;
      }

      // Track successful waitlist signup (legacy tracking)
      if (window.gtag) {
        window.gtag('event', 'signup_success', {
          event_category: 'Waitlist',
          event_label: variant,
        });
      }
    } catch (error) {
      setStatus('error');

      const errorType = getErrorType(error);

      // Handle different error scenarios
      let errorMessage;
      if (error.isNetworkError) {
        errorMessage = 'Unable to connect. Please check your connection and try again.';
      } else if (error.status === 400) {
        errorMessage = 'Please enter a valid email address.';
      } else {
        errorMessage = error.message || 'An error occurred. Please try again.';
      }
      setMessage(errorMessage);

      // Track error event
      analytics.trackWaitlistError(formPosition, errorType, errorMessage);

      // Track failed waitlist signup (legacy tracking)
      if (window.gtag) {
        window.gtag('event', 'signup_error', {
          event_category: 'Waitlist',
          event_label: errorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {(variant === 'embedded' || variant === 'hero' || variant === 'bottom') ? (
        <div className={`form-with-button ${className}`}>
          <form onSubmit={handleSubmit} className="relative">
            <Input
              ref={emailInputRef}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={handleEmailFocus}
              className="form-input pr-36"
              disabled={isSubmitting}
              required
            />
            <Button
              type="submit"
              className="form-submit-embedded"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
          <div aria-live="polite" aria-atomic="true">
            {status === 'success' && (
              <div className="mt-2">
                <span className="inline-block px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-orange-500/40 text-orange-400 text-sm font-medium shadow-lg">{message}</span>
              </div>
            )}
            {status === 'error' && (
              <div className="mt-2">
                <span className="inline-block px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-red-500/40 text-red-400 text-sm font-medium shadow-lg">{message}</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={className}>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
            <Input
              ref={emailInputRef}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={handleEmailFocus}
              className="flex-1"
              disabled={isSubmitting}
              required
            />
            <Button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Subscribing...' : 'Join Waitlist'}
            </Button>
          </form>
          <div aria-live="polite" aria-atomic="true">
            {status === 'success' && (
              <div className="mt-3">
                <span className="inline-block px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-orange-500/40 text-orange-400 text-sm font-medium shadow-lg">{message}</span>
              </div>
            )}
            {status === 'error' && (
              <div className="mt-3">
                <span className="inline-block px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-red-500/40 text-red-400 text-sm font-medium shadow-lg">{message}</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Survey Modal - only render if feature flag is enabled */}
      {FEATURE_FLAGS.SURVEY_MODAL_ENABLED && (
        <SurveyModal
          open={showSurvey}
          onOpenChange={setShowSurvey}
          email={submittedEmail}
          profileToken={profileToken}
          returnFocusRef={emailInputRef}
        />
      )}
    </>
  );
};

export default WaitlistForm;
